import React, {
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { UserRole } from "../types/AppModel";
import { Amplify, Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  setLogoutRedirectForApiResponse,
  setUserForApiRequests,
} from "../services/api";
import { CognitoUser } from "@aws-amplify/auth";
import { useHandleError } from "../hooks/useHandleError";
import {
  getUserProfile,
  signInSuccessRedirectLink,
} from "../services/userService";
import { useLoading } from "./LoadingContext";
import { errorReportingService } from "../services/auth/errorReportingService";
import { sessionPersistenceService } from "../services/auth/sessionPersistenceService";
import {
  getRouteForState,
  getStudentState,
  StudentProfile,
} from "../utils/studentUtils";
import { StudentState } from "../types/StudentModel";

interface ConfirmSignUpParams {
  username: string;
  code: string;
  password?: string; // Optional - if provided, will auto-sign-in
}

interface ConfirmSignUpResult {
  confirmed: boolean;
  signedIn: boolean;
  user?: CognitoUser | null;
  error?: string;
}

const amplifyConfig = {
  aws_project_region: process.env.REACT_APP_COGNITO_REGION || "ap-southeast-2",
  aws_cognito_region: process.env.REACT_APP_COGNITO_REGION || "ap-southeast-2",
  aws_user_pools_id: process.env.REACT_APP_COGNITO_USER_POOL_ID || "",
  aws_user_pools_web_client_id:
    process.env.REACT_APP_COGNITO_APP_CLIENT_ID || "",
};

Amplify.configure(amplifyConfig);

export interface AuthContextProps {
  user: any;
  setUser: React.Dispatch<React.SetStateAction<any | null>>;
  cognitoUser: CognitoUser | null;
  setCognitoUser: React.Dispatch<React.SetStateAction<CognitoUser | null>>;
  isAuthLoading: boolean;
  signIn: (username: string, password: string) => Promise<any>;
  confirmSignUp: (formValues: any) => Promise<any>;
  signUp: (
    FormValues: any
    // firstName: string,
    // lastName: string,
    // email: string,
    // password: string,
    // city: string,
    // country: string,
    // role: UserRole
  ) => Promise<any>;
  signOut: () => Promise<any>;
  completeNewPassword: (
    user: CognitoUser,
    newPassword: string
  ) => Promise<void>;
  resendSignUp: (email: string) => Promise<void>;
}
interface AuthProviderProps {
  children: ReactNode;
}

export interface AuthContext extends AuthContextProps {}

export const AuthContext = React.createContext<AuthContextProps | undefined>(
  undefined
);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { setIsLoading, setLoadingMessage } = useLoading();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const handleError = useHandleError();

  const navigate = useNavigate();

  const [user, setUser] = useState<any | null>(null);
  const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true); // Add this line
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;

    const getCurrentUser = async () => {
      let userData;
      try {
        setIsAuthLoading(true); // Set loading to true at the start

        // No stored session, try Amplify auth
        userData = await Auth.currentAuthenticatedUser();
        errorReportingService.reportDebug(
          "getCurrentUser",
          "Retrieved authenticated user data",
          userData
        );
        if (userData) {
          await applyUser(userData);
        }
      } catch (error) {
        errorReportingService.reportInfo(
          "getCurrentUser",
          "No authenticated user found - user not signed in"
        );
      } finally {
        // Always set loading to false when done, whether successful or not
        setIsAuthLoading(false);
      }
    };

    getCurrentUser();
    fetchedRef.current = true;
  }, [navigate]);

  const applyUser = async (cognitoUser: CognitoUser) => {
    setUserForApiRequests(cognitoUser);
    try {
      const userProfileResponse = await getUserProfile();
      const userProfile = userProfileResponse?.data;

      setUser(userProfile);
      setCognitoUser(cognitoUser);
    } catch (error) {
      handleError(error, "applyUser");
    }
  };

  const navigateBasedOnRole = async (cognitoUser: CognitoUser) => {
    try {
      const attributes = await Auth.userAttributes(cognitoUser);
      const roleAttribute = attributes.find(
        (attr) => attr.Name === "custom:role"
      );
      const role = roleAttribute ? roleAttribute.Value : null;

      const currentPath = window.location.pathname;

      // Don't redirect from sign-in page if we're already on a valid page
      if (
        currentPath &&
        currentPath !== "/auth/sign-in" &&
        !currentPath.includes("/auth/") &&
        currentPath !== "/"
      ) {
        navigate(currentPath);
        return;
      }

      if (role === UserRole.STUDENT) {
        // ================================================
        // STUDENT-SPECIFIC ROUTING
        // ================================================

        // Check 1: Placement test (existing logic)
        if (!user?.placementTest?.result) {
          navigate("/student/placement-test");
          return;
        }

        // Check 2: Student state (NEW)
        const studentState = getStudentState(user as StudentProfile);

        if (studentState !== StudentState.ACTIVE) {
          // Redirect to appropriate error page
          navigate(getRouteForState(studentState));
          return;
        }

        // Student is fully active - go to classroom
        navigate("/student/my-classroom");
        return;
      }

      // ================================================
      // NON-STUDENT ROLES
      // ================================================
      if (role) {
        const redirectLink = signInSuccessRedirectLink(role as UserRole);
        if (redirectLink) {
          navigate(
            redirectLink.startsWith("/") ? redirectLink : `/${redirectLink}`
          );
        }
      }
    } catch (error) {
      handleError(error);
    }
  };

  const signIn = async (username: string, password: string) => {
    setLoadingMessage("Signing in...");
    setIsLoading(true);
    try {
      const cognitoUser = await Auth.signIn(username, password);

      // Handle different challenge types
      if (cognitoUser.challengeName) {
        switch (cognitoUser.challengeName) {
          case "NEW_PASSWORD_REQUIRED":
            // Return user for password reset UI
            return {
              user: cognitoUser,
              challengeType: "NEW_PASSWORD_REQUIRED",
            };

          case "SMS_MFA":
          case "SOFTWARE_TOKEN_MFA":
            // Return user for MFA UI
            return { user: cognitoUser, challengeType: "MFA_REQUIRED" };

          case "SELECT_MFA_TYPE":
            // Return user for MFA selection UI
            return { user: cognitoUser, challengeType: "SELECT_MFA_TYPE" };

          case "MFA_SETUP":
            // Return user for MFA setup UI
            return { user: cognitoUser, challengeType: "MFA_SETUP" };

          case "CUSTOM_CHALLENGE":
            // Return user for custom challenge UI
            return { user: cognitoUser, challengeType: "CUSTOM_CHALLENGE" };

          default:
            // For PASSWORD_VERIFIER or any other challenges
            errorReportingService.reportAuthError({
              message: `Unhandled authentication challenge: ${cognitoUser.challengeName}`,
              operation: "signIn",
              code: "UNHANDLED_CHALLENGE",
              challengeType: cognitoUser.challengeName,
              context: { challengeName: cognitoUser.challengeName },
            });
            // Let Amplify SDK handle it
            return {
              user: cognitoUser,
              challengeType: cognitoUser.challengeName,
            };
        }
      } else {
        // Standard sign-in flow for authenticated users
        await applyUser(cognitoUser);
        await navigateBasedOnRole(cognitoUser);
        return { user: cognitoUser, challengeType: null };
      }
    } catch (error: any) {
      if (error.code === "UserNotConfirmedException") {
        // Handle unconfirmed user
        navigate("/auth/email-confirmation", { state: { email: username } });
      } else if (error.code === "NotAuthorizedException") {
        // Incorrect username or password
        throw new Error("Incorrect username or password");
      } else if (error.code === "UserNotFoundException") {
        // User does not exist
        throw new Error("User does not exist");
      } else {
        // Handle other errors
        throw error;
      }
    } finally {
      setIsLoading(false);
    }
  };
  const signUp = async (formValues: any) => {
    setIsLoading(true);
    try {
      const signUpResult = await Auth.signUp(formValues);
      if (signUpResult && signUpResult.user instanceof CognitoUser) {
        await applyUser(signUpResult.user);
        await navigateBasedOnRole(signUpResult.user);
      } else {
        errorReportingService.reportSystemError({
          message: "Unexpected user type from signUp result",
          operation: "signUp",
          code: "UNEXPECTED_USER_TYPE",
          context: { signUpResult },
        });
      }
    } catch (error: any) {
      enqueueSnackbar(`Error signing up: ${error.message}`, {
        variant: "error",
        preventDuplicate: true,
        key: "signUpError",
        persist: false,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const confirmSignUp = async (
    formValues: ConfirmSignUpParams
  ): Promise<ConfirmSignUpResult> => {
    setIsLoading(true);

    try {
      // Step 1: Confirm the email/sign-up
      const confirmResult = await Auth.confirmSignUp(
        formValues.username.trim().toLowerCase(),
        formValues.code.trim()
      );

      errorReportingService.reportInfo(
        "confirmSignUp",
        "Sign up confirmation successful",
        { username: formValues.username, isSignUpComplete: confirmResult }
      );

      // Step 2: If password provided, attempt auto-sign-in
      if (formValues.password) {
        try {
          const signInResult = await Auth.signIn(
            formValues.username.trim().toLowerCase(),
            formValues.password
          );

          // Check if it's a CognitoUser (successful sign-in)
          if (signInResult && signInResult instanceof CognitoUser) {
            // Check for challenges (MFA, new password required, etc.)
            if (signInResult.challengeName) {
              // Has a challenge - don't auto-complete, let user handle it
              errorReportingService.reportInfo(
                "confirmSignUp",
                "Sign in requires challenge",
                { challengeName: signInResult.challengeName }
              );

              enqueueSnackbar(
                "Account confirmed! Please complete sign-in verification.",
                { variant: "success" }
              );

              return {
                confirmed: true,
                signedIn: false,
                user: signInResult,
              };
            }

            // No challenge - complete the sign-in
            await applyUser(signInResult);

            enqueueSnackbar("Account confirmed and signed in!", {
              variant: "success",
              preventDuplicate: true,
            });

            // Navigate based on role
            await navigateBasedOnRole(signInResult);

            return {
              confirmed: true,
              signedIn: true,
              user: signInResult,
            };
          }
        } catch (signInError: any) {
          // Sign-in failed but confirmation succeeded
          console.warn("Auto sign-in failed after confirmation:", signInError);

          errorReportingService.reportInfo(
            "confirmSignUp",
            "Auto sign-in failed after confirmation",
            { error: signInError.message }
          );

          // Show partial success message
          enqueueSnackbar(
            "Account confirmed! Please sign in with your credentials.",
            { variant: "success" }
          );

          return {
            confirmed: true,
            signedIn: false,
            error: signInError.message,
          };
        }
      }

      // No password provided - just confirmation
      enqueueSnackbar("Account confirmed! Please sign in.", {
        variant: "success",
      });

      return {
        confirmed: true,
        signedIn: false,
      };
    } catch (error: any) {
      errorReportingService.reportAuthError({
        message: "Error confirming sign up",
        operation: "confirmSignUp",
        code: error.code || "CONFIRM_SIGNUP_ERROR",
        context: { error: error.message, username: formValues.username },
      });

      // User-friendly error messages
      const errorMessage = getConfirmSignUpErrorMessage(error);
      enqueueSnackbar(errorMessage, {
        variant: "error",
        preventDuplicate: true,
      });

      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function for user-friendly error messages
  const getConfirmSignUpErrorMessage = (error: any): string => {
    const code = error?.code || error?.name || "";

    switch (code) {
      case "CodeMismatchException":
        return "Invalid verification code. Please check and try again.";
      case "ExpiredCodeException":
        return "Verification code has expired. Please request a new one.";
      case "NotAuthorizedException":
        return "This account is already confirmed. Please sign in.";
      case "UserNotFoundException":
        return "Account not found. Please register first.";
      case "LimitExceededException":
        return "Too many attempts. Please wait and try again.";
      case "AliasExistsException":
        return "An account with this email already exists.";
      default:
        return error.message || "Failed to confirm account. Please try again.";
    }
  };

  const signOut = async () => {
    try {
      await Auth.signOut();

      // Clear local state
      setUser(null);
      setCognitoUser(null);

      navigate("/auth/sign-in");
      enqueueSnackbar(`You are signed out`, {
        variant: "success",
        preventDuplicate: true,
        key: "signedOut",
        persist: false,
      });
    } catch (error: any) {
      errorReportingService.reportSystemError({
        message: "Error signing out",
        operation: "signOut",
        code: error.code || "SIGNOUT_ERROR",
        error: error,
        context: { errorMessage: error.message },
      });
      enqueueSnackbar(`Error signing out: ${error.message}`, {
        variant: "error",
        preventDuplicate: true,
        key: "signUpConfirmingError",
        persist: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const completeNewPassword = async (
    user: CognitoUser,
    newPassword: string
  ) => {
    setIsLoading(true);
    try {
      const updatedUser = await Auth.completeNewPassword(user, newPassword, {});
      await applyUser(updatedUser);
      await navigateBasedOnRole(updatedUser);

      return updatedUser;
    } catch (error: any) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const resendSignUp = async (email: string) => {
    setIsLoading(true);
    try {
      if (!email) {
        enqueueSnackbar("Please log in again to confirm the email.", {
          variant: "error",
        });

        navigate("/auth/sign-in");
        return;
      }

      errorReportingService.reportInfo(
        "resendSignUp",
        "Attempting to resend sign up confirmation",
        { email }
      );
      await Auth.resendSignUp(email);
      enqueueSnackbar(`A new confirmation code has been sent to your email.`, {
        variant: "success",
      });
    } catch (error: any) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        cognitoUser,
        setCognitoUser,
        isAuthLoading,
        signIn,
        signUp,
        confirmSignUp,
        signOut,
        completeNewPassword,
        resendSignUp,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
